import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber,
  Select, 
  Button, 
  message, 
  Tabs,
  Row,
  Col,
  Divider,
  Switch,
  Typography,
  Space,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Collapse,
  Table,
  Badge,
  Spin,
  Alert,
  Empty,
  Upload,
  Avatar,
  Slider
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SettingOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  HolderOutlined
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCreditConfig, getImageCreditConfig } from '../../graphql/queries';
import { updateCreditConfig, updateImageCreditConfig, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';
import { PricingConfigTab } from '../../components/CreditPricing/PricingConfigTab';
import { UnifiedCreditConfig } from './UnifiedCreditConfig';
import { UnifiedCreditConfigV2 } from './UnifiedCreditConfigV2';
import { UnifiedImageCreditConfig } from './UnifiedImageCreditConfig';
import { ImageUnifiedCreditConfigV2 } from './ImageUnifiedCreditConfigV2';
import ModerationAppVersion from './ModerationAppVersion';
import CatalogueStandards from './CatalogueStandards';
import './System.css';

const { Title } = Typography;

const { Option } = Select;

// Enums for adding new items
const AspectRatioEnum = {
  WIDESCREEN: { name: "Widescreen", ratio: "16:9", x: 16, y: 9 },
  PORTRAIT: { name: "Portrait", ratio: "9:16", x: 9, y: 16 },
  SQUARE: { name: "Square", ratio: "1:1", x: 1, y: 1 },
  FULL_SCREEN: { name: "Full Screen", ratio: "4:3", x: 4, y: 3 },
  TABLET: { name: "Tablet", ratio: "3:4", x: 3, y: 4 },
  ULTRAWIDE: { name: "Ultrawide", ratio: "21:9", x: 21, y: 9 }
};

const VideoSecondsEnum = {
  ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5, SIX: 6,
  SEVEN: 7, EIGHT: 8, NINE: 9, TEN: 10, ELEVEN: 11, TWELVE: 12,
  THIRTEEN: 13, FOURTEEN: 14, FIFTEEN: 15, TWENTY: 20
};

const VideoQualityEnum = {
  LOW: { name: "360p", width: 640, height: 360, description: "Low Definition" },
  NORMAL: { name: "480p", width: 864, height: 480, description: "Standard Definition" },
  STANDARD: { name: "512p", width: 910, height: 512, description: "Standard Definition" },
  MEDIUM: { name: "540p", width: 960, height: 540, description: "Medium Definition" },
  ENHANCED: { name: "768p", width: 1365, height: 768, description: "Enhanced Definition" },
  HIGH: { name: "720p", width: 1280, height: 720, description: "High Definition" },
  ULTRA: { name: "1080p", width: 1920, height: 1080, description: "Ultra High Definition" }
};

const QUALITY_ORDER = ['LOW', 'NORMAL', 'STANDARD', 'MEDIUM', 'ENHANCED', 'HIGH', 'ULTRA'];

const getResolutionOptionsOrdered = () =>
  Object.keys(VideoQualityEnum).sort(
    (a, b) => QUALITY_ORDER.indexOf(a) - QUALITY_ORDER.indexOf(b)
  );

const ensurePricingShape = (hasAudio, isOptional, rawMap) => {
  const map = rawMap && typeof rawMap === "object" ? rawMap : {};
  const out = {};

  Object.entries(map).forEach(([resKey, val]) => {
    if (!isResolutionKey(resKey)) return;

    const isDualPricing = hasAudio && isOptional;

    if (isDualPricing) {
      const on =
        typeof val === "number"
          ? val
          : typeof val?.ON === "number"
          ? val.ON
          : undefined;

      const off =
        typeof val?.OFF === "number"
          ? val.OFF
          : undefined;

      out[resKey] = { ON: on, OFF: off };
    } else {
      if (hasAudio) {
        const on =
          typeof val === "number"
            ? val
            : typeof val?.ON === "number"
            ? val.ON
            : undefined;

        out[resKey] = on;
      } else {
        out[resKey] = typeof val === "number" ? val : undefined;
      }
    }
  });

  return out;
};

const getAllowedResolutionKeys = (model) => {
const hasAudio = model?.has_audio_feature === true || model?.has_audio_support === true;
const isOptional = model?.is_audio_optional === true;
const normalized = ensurePricingShape(hasAudio, isOptional, model?.pricing_by_resolution);
  return Object.keys(normalized || {}); // sadece create/edit’te eklenen satırlar
};

const isResolutionKey = (k) => Object.prototype.hasOwnProperty.call(VideoQualityEnum, k);

const SortableModelWrapper = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bfbfbf',
          background: '#fafafa',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
};

const SystemPage = () => {
  const getInitialTab = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('systemPageActiveTab') || 'unifiedCreditConfig';
    }
    return 'unifiedCreditConfig';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());

  const [creditConfig, setCreditConfig] = useState({
    image_to_video: {
      models: []
    },
    text_to_video: {
      models: []
    }
  });

  // Dialog states for adding items
  const [addModelDialog, setAddModelDialog] = useState({
    visible: false,
    operationType: '',
    modelId: '',
    modelName: '',
    modelDescription: '',
    modelEndpoint: '',
    icon: '',
    iconFile: null,
    costPreview: 1,
    hasAudioFeature: true,
    isAudioOptional: true,
    isDefault: false,
    pricingByResolution: {},
    defaultValues: {
      duration: null,
      resolution: null,
      aspectRatio: null,
      isAudio: false
    }
  });

  const [editModelDialog, setEditModelDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    modelId: '',
    modelName: '',
    modelDescription: '',
    modelEndpoint: '',
    icon: '',
    iconFile: null,
    costPreview: 1,
    hasAudioFeature: true,
    isAudioOptional: true,
    isDefault: false,
    pricingByResolution: {},
    defaultValues: {
      duration: null,
      resolution: null,
      aspectRatio: null,
      isAudio: false
    }
  });

  const [addDurationDialog, setAddDurationDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    ratioIndex: 0,
    resolutionIndex: 0,
    selectedDuration: 'FIVE',
  });

  const [addResolutionDialog, setAddResolutionDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    ratioIndex: 0,
    selectedQuality: 'NORMAL'
  });

  const [addAspectRatioDialog, setAddAspectRatioDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    selectedRatio: 'WIDESCREEN',
    width: 1280,
    height: 720
  });

  const [imageCreditConfig, setImageCreditConfig] = useState({
    text_to_image: { models: [] },
    image_to_image: { models: [] }
  });

  const [addImageModelDialog, setAddImageModelDialog] = useState({
    visible: false,
    operationType: '',
    modelId: '',
    modelName: '',
    modelDescription: '',
    modelEndpoint: '',
    icon: '',
    iconFile: null,
    costPreview: 1,
    isDefault: false,
    hasResolution: false,
  });

  const [editImageModelDialog, setEditImageModelDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    modelId: '',
    modelName: '',
    modelDescription: '',
    modelEndpoint: '',
    icon: '',
    iconFile: null,
    costPreview: 1,
    isDefault: false,
    hasResolution: false,
  });

  const [addImageAspectRatioDialog, setAddImageAspectRatioDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    selectedRatio: 'WIDESCREEN',
    actualCost: 0,
  });

  const [addImageResolutionDialog, setAddImageResolutionDialog] = useState({
    visible: false,
    operationType: '',
    modelIndex: 0,
    ratioIndex: 0,
    resolutionName: '',
    resolutionValue: '',
    actualCost: 0,
  });

  const [updateCreditConfigMutation] = useMutation(updateCreditConfig);
  const [updateImageCreditConfigMutation] = useMutation(updateImageCreditConfig);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const { data: creditConfigData, loading: creditConfigLoading, refetch: refetchCreditConfig } = useQuery(getCreditConfig);
  const { data: imageCreditConfigData, loading: imageCreditConfigLoading, refetch: refetchImageCreditConfig } = useQuery(getImageCreditConfig);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const creditModelOrderRef = useRef(
    (() => { try { return JSON.parse(localStorage.getItem('creditModelOrder')) || null; } catch { return null; } })()
  );
  const imageModelOrderRef = useRef(
    (() => { try { return JSON.parse(localStorage.getItem('imageModelOrder')) || null; } catch { return null; } })()
  );

  const saveCreditOrder = (order) => {
    creditModelOrderRef.current = order;
    try { localStorage.setItem('creditModelOrder', JSON.stringify(order)); } catch {}
  };

  const saveImageOrder = (order) => {
    imageModelOrderRef.current = order;
    try { localStorage.setItem('imageModelOrder', JSON.stringify(order)); } catch {}
  };

  const applyModelOrder = (config, orderRef, operationTypes) => {
    if (!orderRef) return config;
    operationTypes.forEach(op => {
      if (config[op]?.models && orderRef[op]?.length) {
        config[op].models.sort((a, b) => {
          const ai = orderRef[op].indexOf(a.id);
          const bi = orderRef[op].indexOf(b.id);
          return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
        });
      }
    });
    return config;
  };

  const handleCreditModelDragEnd = (operationType, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const models = newConfig[operationType].models;
      const oldIndex = models.findIndex(m => m.id === active.id);
      const newIndex = models.findIndex(m => m.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        newConfig[operationType].models = arrayMove(models, oldIndex, newIndex);
      }
      saveCreditOrder({
        image_to_video: newConfig.image_to_video?.models?.map(m => m.id) || [],
        text_to_video: newConfig.text_to_video?.models?.map(m => m.id) || [],
      });
      return newConfig;
    });
  };

  const handleImageModelDragEnd = (operationType, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setImageCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const models = newConfig[operationType].models;
      const oldIndex = models.findIndex(m => m.id === active.id);
      const newIndex = models.findIndex(m => m.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        newConfig[operationType].models = arrayMove(models, oldIndex, newIndex);
      }
      saveImageOrder({
        text_to_image: newConfig.text_to_image?.models?.map(m => m.id) || [],
        image_to_image: newConfig.image_to_image?.models?.map(m => m.id) || [],
      });
      return newConfig;
    });
  };

  useEffect(() => {
    if (creditConfigData?.getModelList) {
      const config = JSON.parse(JSON.stringify(creditConfigData.getModelList));
      ['image_to_video', 'text_to_video'].forEach(operationType => {
        if (config[operationType]?.models) {
          config[operationType].models = config[operationType].models.map(model => {
            if (model.has_audio_support !== undefined && model.has_audio_feature === undefined) {
              model.has_audio_feature = model.has_audio_support;
            }
            if (model.is_audio_optional === undefined) model.is_audio_optional = true;
            if (model.is_default === undefined) model.is_default = false;
            return model;
          });
        }
      });
      
      applyModelOrder(config, creditModelOrderRef.current, ['image_to_video', 'text_to_video']);
      setCreditConfig(config);
    }
    if (imageCreditConfigData?.getImageModelList) {
      const config = JSON.parse(JSON.stringify(imageCreditConfigData.getImageModelList));
      ['text_to_image', 'image_to_image'].forEach(operationType => {
        if (config[operationType]?.models) {
          config[operationType].models = config[operationType].models.map(model => {
            if (model.is_default === undefined) model.is_default = false;
            return model;
          });
        }
      });
      applyModelOrder(config, imageModelOrderRef.current, ['text_to_image', 'image_to_image']);
      setImageCreditConfig(config);
    }
  }, [creditConfigData, imageCreditConfigData]);

  const handleCreditConfigUpdate = async () => {
    try {
      saveCreditOrder({
        image_to_video: creditConfig.image_to_video?.models?.map(m => m.id) || [],
        text_to_video: creditConfig.text_to_video?.models?.map(m => m.id) || [],
      });

      await updateCreditConfigMutation({
        variables: { creditJson: creditConfig },
      });

      message.success("Saved. Recalculating costs...");

      const refreshed = await refetchCreditConfig();

      if (refreshed?.data?.getModelList) {
        const config = JSON.parse(JSON.stringify(refreshed.data.getModelList));
        ["image_to_video", "text_to_video"].forEach((operationType) => {
          if (config[operationType]?.models) {
            config[operationType].models = config[operationType].models.map((model) => {
              if (model.has_audio_support !== undefined && model.has_audio_feature === undefined) {
                model.has_audio_feature = model.has_audio_support;
              }
              if (model.is_audio_optional === undefined) model.is_audio_optional = true;
              if (model.is_default === undefined) model.is_default = false;
              return model;
            });
          }
        });
        applyModelOrder(config, creditModelOrderRef.current, ['image_to_video', 'text_to_video']);
        setCreditConfig(config);
      }

      message.success("Credit configuration updated & costs recalculated.");
    } catch (error) {
      message.error("Update failed: " + error.message);
    }
  };

  // Helper function to count default models
  const countDefaultModels = (operationType) => {
    const models = creditConfig[operationType]?.models || [];
    return models.filter(model => model.is_default === true).length;
  };

  // Add model
  const handleAddModel = async (operationType, modelId, modelName, modelDescription, modelEndpoint, icon, iconFile, costPreview, hasAudioFeature, isAudioOptional, isDefault, defaultValues,pricingByResolution) => {
    try {
      // Validate max 2 default models per operation type
      if (isDefault) {
        const defaultCount = countDefaultModels(operationType);
        if (defaultCount >= 2) {
          message.error(`Cannot mark more than 2 models as default for ${operationType}. Please unmark another default model first.`);
          return;
        }
      }

      let iconUrl = icon;

      // Upload icon if a new file is provided
      if (iconFile) {
        const { data: signedUrlData } = await getSignedUrlMutation({
          variables: { fileName: iconFile.name }
        });

        const uploadResult = await uploadToR2(
          iconFile,
          signedUrlData.getSignedUrl.url,
          signedUrlData.getSignedUrl.contentType
        );

        if (!uploadResult.success) {
          message.error('Icon upload failed: ' + uploadResult.message);
          return;
        }

        const { data: objectUrlData } = await getObjectUrlMutation({
          variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' }
        });

        iconUrl = objectUrlData.getObjectUrl;
      }

      // Build default_values only with non-null values
      const modelDefaultValues = {};
      if (defaultValues?.duration) modelDefaultValues.duration = defaultValues.duration;
      if (defaultValues?.resolution) modelDefaultValues.resolution = defaultValues.resolution;
      if (defaultValues?.aspectRatio) modelDefaultValues.aspectRatio = defaultValues.aspectRatio;
      // Only include isAudio if model has audio feature
      if (hasAudioFeature && defaultValues?.isAudio !== undefined) {
        modelDefaultValues.isAudio = defaultValues.isAudio;
      }

      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        const newModel = {
          id: modelId,
          name: modelName,
          description: modelDescription || '',
          model_endpoint: modelEndpoint || '',
          icon: iconUrl || '',
          cost_preview: costPreview,
          has_audio_feature: hasAudioFeature,
          is_audio_optional: isAudioOptional,
          is_default: isDefault || false,
          pricing_by_resolution: pricingByResolution || {},
          aspect_ratios: []
        };
        
        // Only add default_values if there are any values set
        if (Object.keys(modelDefaultValues).length > 0) {
          newModel.default_values = modelDefaultValues;
        }
        
        newConfig[operationType].models.push(newModel);
        return newConfig;
      });
      message.success('Model added successfully');
    } catch (error) {
      message.error('Failed to add model: ' + error.message);
    }
  };

  // Edit model
  const handleEditModel = async (operationType, modelIndex, modelId, modelName, modelDescription, modelEndpoint, icon, iconFile, costPreview, hasAudioFeature, isAudioOptional, isDefault, defaultValues, pricingByResolution) => {
    try {
      // Validate max 2 default models per operation type
      if (isDefault) {
        const currentModel = creditConfig[operationType]?.models[modelIndex];
        const defaultCount = countDefaultModels(operationType);
        // If current model is not default and we're trying to make it default, check count
        if (!currentModel?.is_default && defaultCount >= 2) {
          message.error(`Cannot mark more than 2 models as default for ${operationType}. Please unmark another default model first.`);
          return;
        }
      }

      let iconUrl = icon;

      // Upload icon if a new file is provided
      if (iconFile) {
        const { data: signedUrlData } = await getSignedUrlMutation({
          variables: { fileName: iconFile.name }
        });

        const uploadResult = await uploadToR2(
          iconFile,
          signedUrlData.getSignedUrl.url,
          signedUrlData.getSignedUrl.contentType
        );

        if (!uploadResult.success) {
          message.error('Icon upload failed: ' + uploadResult.message);
          return;
        }

        const { data: objectUrlData } = await getObjectUrlMutation({
          variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' }
        });

        iconUrl = objectUrlData.getObjectUrl;
      }

      // Build default_values only with non-null values
      const modelDefaultValues = {};
      if (defaultValues?.duration) modelDefaultValues.duration = defaultValues.duration;
      if (defaultValues?.resolution) modelDefaultValues.resolution = defaultValues.resolution;
      if (defaultValues?.aspectRatio) modelDefaultValues.aspectRatio = defaultValues.aspectRatio;
      // Only include isAudio if model has audio feature
      if (hasAudioFeature && defaultValues?.isAudio !== undefined) {
        modelDefaultValues.isAudio = defaultValues.isAudio;
      }

      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].id = modelId;
        newConfig[operationType].models[modelIndex].name = modelName;
        newConfig[operationType].models[modelIndex].description = modelDescription || '';
        newConfig[operationType].models[modelIndex].model_endpoint = modelEndpoint || '';
        newConfig[operationType].models[modelIndex].icon = iconUrl || '';
        newConfig[operationType].models[modelIndex].cost_preview = costPreview;
        newConfig[operationType].models[modelIndex].has_audio_feature = hasAudioFeature;
        newConfig[operationType].models[modelIndex].is_audio_optional = isAudioOptional;
        newConfig[operationType].models[modelIndex].is_default = isDefault || false;
        newConfig[operationType].models[modelIndex].pricing_by_resolution = pricingByResolution || {};

        const updatedModel = newConfig[operationType].models[modelIndex];
        const hasAudio = updatedModel.has_audio_feature === true;
        const isOptional = updatedModel.is_audio_optional === true;
        const isDualPricing = hasAudio && isOptional;

        updatedModel.aspect_ratios?.forEach((aspectRatio) => {
          aspectRatio.resolutions?.forEach((resolution) => {
            const resKey = resolution?.resolution?.api_enum_value;
            if (!resKey || !pricingByResolution) return;

            const pricing = pricingByResolution[resKey];

            if (pricing !== undefined) {
              if (typeof pricing === "object") {
                resolution.pricing_per_second = {
                  audio_on: pricing.ON,
                  audio_off: pricing.OFF
                };
              } else {
                resolution.pricing_per_second = pricing;
              }
            }
          });
        });
        
        // Only add default_values if there are any values set
        if (Object.keys(modelDefaultValues).length > 0) {
          newConfig[operationType].models[modelIndex].default_values = modelDefaultValues;
        } else {
          // Remove default_values if no values are set
          delete newConfig[operationType].models[modelIndex].default_values;
        }
        
        return newConfig;
      });
      message.success('Model updated successfully');
    } catch (error) {
      message.error('Failed to update model: ' + error.message);
    }
  };

  // Remove model
  const handleRemoveModel = (operationType, modelIndex) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[operationType].models = newConfig[operationType].models.filter((_, idx) => idx !== modelIndex);
      return newConfig;
    });
    message.success('Model removed successfully');
  };

  // Remove aspect ratio
  const handleRemoveAspectRatio = (operationType, modelIndex, ratioIndex) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[operationType].models[modelIndex].aspect_ratios = 
        newConfig[operationType].models[modelIndex].aspect_ratios.filter((_, idx) => idx !== ratioIndex);
      return newConfig;
    });
    message.success('Aspect ratio removed successfully');
  };

  // Remove resolution
  const handleRemoveResolution = (operationType, modelIndex, ratioIndex, resolutionIndex) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions = 
        newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions.filter((_, idx) => idx !== resolutionIndex);
      return newConfig;
    });
    message.success('Resolution removed successfully');
  };

  // Remove duration
  const handleRemoveDuration = (operationType, modelIndex, ratioIndex, resolutionIndex, durationIndex) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions[resolutionIndex].durations = 
        newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions[resolutionIndex].durations.filter((_, idx) => idx !== durationIndex);
      return newConfig;
    });
    message.success('Duration removed successfully');
  };

  const handleAddDuration = (operationType, modelIndex, ratioIndex, resolutionIndex, secondsEnum) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const model = newConfig[operationType].models[modelIndex];
      const seconds = VideoSecondsEnum[secondsEnum];

      const newDuration = {
        duration: {
          name: `${seconds} seconds`,
          seconds: seconds,
          short_name: `${seconds}s`,
          api_enum_value: secondsEnum,
        },
        // cost artık UI'da girilmiyor.
        // Backend normalizeAndRecalculate save sonrası bunu overwrite edecek.
      };

      model.aspect_ratios[ratioIndex].resolutions[resolutionIndex].durations.push(newDuration);
      return newConfig;
    });

    message.success('Duration added successfully');
  };

  // Add resolution
  const handleAddResolution = (operationType, modelIndex, ratioIndex, qualityEnum) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const model = newConfig[operationType].models[modelIndex];
      const quality = VideoQualityEnum[qualityEnum];
      const pricingMap = model.pricing_by_resolution || {};
      const hasAudio = model.has_audio_feature === true || model.has_audio_support === true;
      const isOptional = model.is_audio_optional === true;
      const isDualPricing = hasAudio && isOptional;

      const newResolution = {
        resolution: {
          name: quality.name,
          width: quality.width,
          height: quality.height,
          short_name: quality.name,
          api_enum_value: qualityEnum
        },
        durations: []
      };

      const p = pricingMap?.[qualityEnum];

      if (p !== undefined) {
        if (typeof p === "object") {
          newResolution.pricing_per_second = {
            audio_on: p.ON,
            audio_off: p.OFF
          };
        } else {
          newResolution.pricing_per_second = p;
        }
      }

            model.aspect_ratios[ratioIndex].resolutions.push(newResolution);

            return newConfig;
          });
          message.success('Resolution added successfully');
        };

  // Add aspect ratio
  const handleAddAspectRatio = (operationType, modelIndex, ratioEnum, width, height) => {
    setCreditConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const model = newConfig[operationType].models[modelIndex];
      const ratio = AspectRatioEnum[ratioEnum];

      const newAspectRatio = {
        ratio: {
          name: ratio.ratio,
          width: width,
          height: height,
          short_name: ratio.ratio,
          api_enum_value: ratioEnum,
          aspect_ratio_x: ratio.x,
          aspect_ratio_y: ratio.y
        },
        resolutions: []
      };

      model.aspect_ratios.push(newAspectRatio);

      return newConfig;
    });
    message.success('Aspect ratio added successfully');
  };

  const handleToggleAspectRatioGlobal = (operationType, modelIndex, ratioKey) => {
    const model = creditConfig[operationType]?.models?.[modelIndex];
    if (!model) return;
    const activeRatioKeys = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);
    if (activeRatioKeys.has(ratioKey)) {
      const ratioIndex = model.aspect_ratios.findIndex(ar => ar.ratio?.api_enum_value === ratioKey);
      if (ratioIndex !== -1) handleRemoveAspectRatio(operationType, modelIndex, ratioIndex);
    } else {
      const ratio = AspectRatioEnum[ratioKey];
      if (!ratio) return;
      const templateResolutions = model.aspect_ratios?.[0]?.resolutions
        ? JSON.parse(JSON.stringify(model.aspect_ratios[0].resolutions))
        : [];
      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].aspect_ratios.push({
          ratio: { name: ratio.ratio, short_name: ratio.ratio, api_enum_value: ratioKey, aspect_ratio_x: ratio.x, aspect_ratio_y: ratio.y },
          resolutions: templateResolutions,
        });
        return newConfig;
      });
    }
  };

  const handleToggleResolutionGlobal = (operationType, modelIndex, resKey) => {
    const model = creditConfig[operationType]?.models?.[modelIndex];
    if (!model) return;
    const activeResolutionKeys = new Set();
    model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(r => activeResolutionKeys.add(r.resolution?.api_enum_value)));
    if (activeResolutionKeys.has(resKey)) {
      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions = (ar.resolutions || []).filter(r => r.resolution?.api_enum_value !== resKey);
        });
        return newConfig;
      });
    } else {
      const quality = VideoQualityEnum[resKey];
      if (!quality) return;
      const pricingMap = model.pricing_by_resolution || {};
      const activeDurationKeys = new Set();
      model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(res => res.durations?.forEach(d => activeDurationKeys.add(d.duration?.api_enum_value))));
      const durations = [...activeDurationKeys]
        .map(dk => ({ duration: { api_enum_value: dk, seconds: VideoSecondsEnum[dk], name: `${VideoSecondsEnum[dk]} seconds`, short_name: `${VideoSecondsEnum[dk]}s` } }))
        .sort((a, b) => a.duration.seconds - b.duration.seconds);
      const newRes = {
        resolution: { name: quality.name, width: quality.width, height: quality.height, short_name: quality.name, api_enum_value: resKey },
        durations,
      };
      const p = pricingMap?.[resKey];
      if (p !== undefined) {
        newRes.pricing_per_second = typeof p === 'object' ? { audio_on: p.ON, audio_off: p.OFF } : p;
      }
      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].aspect_ratios?.forEach(ar => {
          if (!ar.resolutions?.some(r => r.resolution?.api_enum_value === resKey)) {
            ar.resolutions = [...(ar.resolutions || []), JSON.parse(JSON.stringify(newRes))];
          }
        });
        return newConfig;
      });
    }
  };

  const handleToggleDurationGlobal = (operationType, modelIndex, durationKey) => {
    const model = creditConfig[operationType]?.models?.[modelIndex];
    if (!model) return;
    const activeDurationKeys = new Set();
    model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(res => res.durations?.forEach(d => activeDurationKeys.add(d.duration?.api_enum_value))));
    const seconds = VideoSecondsEnum[durationKey];
    if (seconds === undefined) return;
    if (activeDurationKeys.has(durationKey)) {
      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions?.forEach(res => {
            res.durations = (res.durations || []).filter(d => d.duration?.api_enum_value !== durationKey);
          });
        });
        return newConfig;
      });
    } else {
      const newDur = { duration: { api_enum_value: durationKey, seconds, name: `${seconds} seconds`, short_name: `${seconds}s` } };
      setCreditConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        newConfig[operationType].models[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions?.forEach(res => {
            if (!res.durations?.some(d => d.duration?.api_enum_value === durationKey)) {
              res.durations = [...(res.durations || []), JSON.parse(JSON.stringify(newDur))].sort(
                (a, b) => a.duration.seconds - b.duration.seconds
              );
            }
          });
        });
        return newConfig;
      });
    }
  };

  // ==================== IMAGE CREDIT CONFIG HANDLERS ====================

  const handleImageCreditConfigUpdate = async () => {
    try {
      saveImageOrder({
        text_to_image: imageCreditConfig.text_to_image?.models?.map(m => m.id) || [],
        image_to_image: imageCreditConfig.image_to_image?.models?.map(m => m.id) || [],
      });

      await updateImageCreditConfigMutation({
        variables: { creditJson: imageCreditConfig },
      });

      message.success("Saved. Recalculating image costs...");

      const refreshed = await refetchImageCreditConfig();

      if (refreshed?.data?.getImageModelList) {
        const config = JSON.parse(JSON.stringify(refreshed.data.getImageModelList));
        ['text_to_image', 'image_to_image'].forEach((operationType) => {
          if (config[operationType]?.models) {
            config[operationType].models = config[operationType].models.map((model) => {
              if (model.is_default === undefined) model.is_default = false;
              return model;
            });
          }
        });
        applyModelOrder(config, imageModelOrderRef.current, ['text_to_image', 'image_to_image']);
        setImageCreditConfig(config);
      }

      message.success("Image credit configuration updated & costs recalculated.");
    } catch (error) {
      message.error("Update failed: " + error.message);
    }
  };

  const countImageDefaultModels = (operationType) => {
    const models = imageCreditConfig[operationType]?.models || [];
    return models.filter(model => model.is_default === true).length;
  };

  const handleAddImageModel = async (operationType) => {
    const { modelId, modelName, modelDescription, modelEndpoint, icon, iconFile, costPreview, isDefault, hasResolution } = addImageModelDialog;
    try {
      if (!modelId || !modelName) {
        message.error('Model ID and Name are required');
        return;
      }

      if (isDefault) {
        const defaultCount = countImageDefaultModels(operationType);
        if (defaultCount >= 2) {
          message.error(`Cannot mark more than 2 models as default for ${operationType}.`);
          return;
        }
      }

      let iconUrl = icon;

      if (iconFile) {
        const { data: signedUrlData } = await getSignedUrlMutation({
          variables: { fileName: iconFile.name }
        });
        const uploadResult = await uploadToR2(
          iconFile,
          signedUrlData.getSignedUrl.url,
          signedUrlData.getSignedUrl.contentType
        );
        if (!uploadResult.success) {
          message.error('Icon upload failed: ' + uploadResult.message);
          return;
        }
        const { data: objectUrlData } = await getObjectUrlMutation({
          variables: { eTag: uploadResult.eTag || 'etag', key: signedUrlData.getSignedUrl.id }
        });
        iconUrl = objectUrlData.getObjectUrl;
      }

      const newConfig = { ...imageCreditConfig };
      if (!newConfig[operationType]) {
        newConfig[operationType] = { models: [] };
      }

      newConfig[operationType].models.push({
        id: modelId,
        name: modelName,
        description: modelDescription,
        model_endpoint: modelEndpoint,
        icon: iconUrl,
        cost_preview: costPreview,
        is_default: isDefault,
        has_resolution: hasResolution,
        aspect_ratios: [],
      });

      setImageCreditConfig({ ...newConfig });
      setAddImageModelDialog({ ...addImageModelDialog, visible: false, modelId: '', modelName: '', modelDescription: '', modelEndpoint: '', icon: '', iconFile: null, costPreview: 1, isDefault: false, hasResolution: false });
      message.success(`Model "${modelName}" added`);
    } catch (error) {
      message.error('Failed to add model: ' + error.message);
    }
  };

  const handleEditImageModel = async (operationType) => {
    const { modelIndex, modelId, modelName, modelDescription, modelEndpoint, icon, iconFile, costPreview, isDefault, hasResolution } = editImageModelDialog;
    try {
      if (!modelId || !modelName) {
        message.error('Model ID and Name are required');
        return;
      }

      let iconUrl = icon;
      if (iconFile) {
        const { data: signedUrlData } = await getSignedUrlMutation({
          variables: { fileName: iconFile.name }
        });
        const uploadResult = await uploadToR2(
          iconFile,
          signedUrlData.getSignedUrl.url,
          signedUrlData.getSignedUrl.contentType
        );
        if (!uploadResult.success) {
          message.error('Icon upload failed: ' + uploadResult.message);
          return;
        }
        const { data: objectUrlData } = await getObjectUrlMutation({
          variables: { eTag: uploadResult.eTag || 'etag', key: signedUrlData.getSignedUrl.id }
        });
        iconUrl = objectUrlData.getObjectUrl;
      }

      const newConfig = { ...imageCreditConfig };
      const model = newConfig[operationType].models[modelIndex];
      model.id = modelId;
      model.name = modelName;
      model.description = modelDescription;
      model.model_endpoint = modelEndpoint;
      model.icon = iconUrl;
      model.cost_preview = costPreview;
      model.is_default = isDefault;
      model.has_resolution = hasResolution;

      setImageCreditConfig({ ...newConfig });
      setEditImageModelDialog({ ...editImageModelDialog, visible: false });
      message.success(`Model "${modelName}" updated`);
    } catch (error) {
      message.error('Failed to update model: ' + error.message);
    }
  };

  const handleRemoveImageModel = (operationType, modelIndex) => {
    const newConfig = { ...imageCreditConfig };
    newConfig[operationType].models.splice(modelIndex, 1);
    setImageCreditConfig({ ...newConfig });
    message.success('Model removed');
  };

  const handleAddImageAspectRatio = (operationType) => {
    const { modelIndex, selectedRatio, actualCost } = addImageAspectRatioDialog;
    const ratioInfo = AspectRatioEnum[selectedRatio];
    if (!ratioInfo) return;

    const newConfig = { ...imageCreditConfig };
    const model = newConfig[operationType].models[modelIndex];
    if (!model.aspect_ratios) model.aspect_ratios = [];

    const exists = model.aspect_ratios.find(ar => ar.ratio.api_enum_value === selectedRatio);
    if (exists) {
      message.error('This aspect ratio already exists');
      return;
    }

    model.aspect_ratios.push({
      ratio: {
        name: ratioInfo.ratio,
        short_name: ratioInfo.ratio,
        api_enum_value: selectedRatio,
      },
      actual_cost: actualCost || 0,
      cost: 0,
    });

    setImageCreditConfig({ ...newConfig });
    setAddImageAspectRatioDialog({ ...addImageAspectRatioDialog, visible: false });
    message.success('Aspect ratio added');
  };

  const handleRemoveImageAspectRatio = (operationType, modelIndex, ratioIndex) => {
    const newConfig = { ...imageCreditConfig };
    newConfig[operationType].models[modelIndex].aspect_ratios.splice(ratioIndex, 1);
    setImageCreditConfig({ ...newConfig });
    message.success('Aspect ratio removed');
  };

  const handleImageAspectRatioCostChange = (operationType, modelIndex, ratioIndex, field, value) => {
    const newConfig = { ...imageCreditConfig };
    newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex][field] = value;
    setImageCreditConfig({ ...newConfig });
  };

  const handleAddImageResolution = (operationType) => {
    const { modelIndex, ratioIndex, resolutionName, resolutionValue, actualCost } = addImageResolutionDialog;
    if (!resolutionName || !resolutionValue) {
      message.error('Resolution name and value are required');
      return;
    }

    const newConfig = { ...imageCreditConfig };
    const aspect = newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex];
    if (!aspect.resolutions) aspect.resolutions = [];

    const exists = aspect.resolutions.find(r => r.resolution.api_enum_value === resolutionValue);
    if (exists) {
      message.error('This resolution already exists');
      return;
    }

    aspect.resolutions.push({
      resolution: {
        name: resolutionName,
        api_enum_value: resolutionValue,
      },
      actual_cost: actualCost || 0,
      cost: 0,
    });

    setImageCreditConfig({ ...newConfig });
    setAddImageResolutionDialog({ ...addImageResolutionDialog, visible: false });
    message.success('Resolution added');
  };

  const handleRemoveImageResolution = (operationType, modelIndex, ratioIndex, resIndex) => {
    const newConfig = { ...imageCreditConfig };
    newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions.splice(resIndex, 1);
    setImageCreditConfig({ ...newConfig });
    message.success('Resolution removed');
  };

  const handleImageResolutionCostChange = (operationType, modelIndex, ratioIndex, resIndex, field, value) => {
    const newConfig = { ...imageCreditConfig };
    newConfig[operationType].models[modelIndex].aspect_ratios[ratioIndex].resolutions[resIndex][field] = value;
    setImageCreditConfig({ ...newConfig });
  };

  // ==================== IMAGE CREDIT CONFIG RENDER ====================

  const renderImageCreditConfigForm = () => {
    if (imageCreditConfigLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading image configuration...</p>
        </div>
      );
    }

    const operationTypes = [
      { key: 'text_to_image', label: 'Text to Image', icon: '🖼️' },
      { key: 'image_to_image', label: 'Image to Image', icon: '🎨' }
    ];

    return (
      <div style={{ maxWidth: '1600px' }}>
        <div
          style={{
            marginBottom: 14,
            border: 'none'
          }}
        >
          <Title level={2} style={{ color: 'black', margin: 0, marginBottom: 8 }}>
           Image Credit Configuration
          </Title>
          <p style={{ margin: 0, color: 'white', opacity: 0.95, fontSize: '16px' }}>
            Configure image generation models, aspect ratios, and credit costs
          </p>
        </div>

        {operationTypes.map(operationTypeObj => {
          const operation = imageCreditConfig[operationTypeObj.key];
          if (!operation) return null;

          return (
            <Card
              key={operationTypeObj.key}
              title={
                <Space size="large">
                  <span style={{ fontSize: '20px' }}>{operationTypeObj.icon}</span>
                  <span style={{ fontSize: '18px', fontWeight: 600 }}>{operationTypeObj.label}</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              {(!operation.models || operation.models.length === 0) ? (
                <Empty description="No models configured yet" style={{ padding: '40px' }} />
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleImageModelDragEnd(operationTypeObj.key, e)}>
                  <SortableContext items={operation.models.map(m => m.id)} strategy={verticalListSortingStrategy}>
                    {operation.models.map((model, modelIndex) => (
                      <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse style={{ flex: 1, border: 'none', borderRadius: 0 }} expandIconPosition="end" items={[{
                    key: '0',
                    label: (
                      <Space align="start">
                        {model.icon ? (
                          <img
                            src={model.icon}
                            alt={model.name}
                            style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover', backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9' }}
                          />
                        ) : (
                          <Avatar size={48} shape="square" style={{ marginTop: 4, backgroundColor: '#f0f0f0' }} icon={<SettingOutlined />} />
                        )}
                        <div>
                          <Space wrap>
                            <Badge status="processing" color="#52c41a" />
                            <strong>{model.name}</strong>
                            <Tag color="purple">{model.id}</Tag>
                            {model.is_default && <Tag color="gold">Default Model</Tag>}
                            {model.has_resolution && <Tag color="cyan">Resolution Support</Tag>}
                          </Space>
                          {model.description && (
                            <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>{model.description}</div>
                          )}
                        </div>
                      </Space>
                    ),
                    extra: (
                      <Space>
                        <Button
                          size="small"
                          onClick={() => setEditImageModelDialog({
                            visible: true,
                            operationType: operationTypeObj.key,
                            modelIndex,
                            modelId: model.id,
                            modelName: model.name,
                            modelDescription: model.description || '',
                            modelEndpoint: model.model_endpoint || '',
                            icon: model.icon || '',
                            iconFile: null,
                            costPreview: model.cost_preview || 1,
                            isDefault: model.is_default || false,
                            hasResolution: model.has_resolution || false,
                          })}
                        >
                          Edit
                        </Button>
                        <Popconfirm
                          title="Remove this model?"
                          onConfirm={() => handleRemoveImageModel(operationTypeObj.key, modelIndex)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                        </Popconfirm>
                      </Space>
                    ),
                    children: (
                      <>
                        {/* Aspect Ratios */}
                    <Divider orientation="left">Aspect Ratios & Pricing</Divider>

                    {(!model.aspect_ratios || model.aspect_ratios.length === 0) ? (
                      <Empty description="No aspect ratios configured" />
                    ) : model.has_resolution ? (
                      /* Resolution-based pricing: aspect ratio → resolution → cost */
                      <Collapse
                        items={model.aspect_ratios.map((ar, ratioIndex) => ({
                          key: ratioIndex,
                          label: (
                            <Space>
                              <Tag color="blue">{ar.ratio.name}</Tag>
                              <span style={{ fontSize: '12px', color: '#999' }}>({ar.ratio.api_enum_value})</span>
                              <Tag>{(ar.resolutions || []).length} resolution(s)</Tag>
                            </Space>
                          ),
                          extra: (
                            <Popconfirm
                              title="Remove this aspect ratio?"
                              onConfirm={(e) => { e.stopPropagation(); handleRemoveImageAspectRatio(operationTypeObj.key, modelIndex, ratioIndex); }}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button danger size="small" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>Remove</Button>
                            </Popconfirm>
                          ),
                          children: (
                            <>
                            {(!ar.resolutions || ar.resolutions.length === 0) ? (
                              <Empty description="No resolutions configured" />
                            ) : (
                              <Table
                                dataSource={ar.resolutions}
                                rowKey={(_, idx) => idx}
                                pagination={false}
                                size="small"
                                columns={[
                                  {
                                    title: 'Resolution',
                                    key: 'resolution',
                                    render: (_, record) => (
                                      <Space>
                                        <Tag color="geekblue">{record.resolution.api_enum_value}</Tag>
                                        <span>{record.resolution.name}</span>
                                      </Space>
                                    ),
                                  },
                                  {
                                    title: 'Actual Cost ($)',
                                    key: 'actual_cost',
                                    width: 150,
                                    render: (_, record, resIndex) => (
                                      <InputNumber
                                        value={record.actual_cost ?? 0}
                                        onChange={(val) => handleImageResolutionCostChange(operationTypeObj.key, modelIndex, ratioIndex, resIndex, 'actual_cost', val)}
                                        min={0}
                                        step={0.001}
                                        style={{ width: '100%' }}
                                        prefix="$"
                                      />
                                    ),
                                  },
                                  {
                                    title: 'Credit Cost',
                                    key: 'cost',
                                    width: 150,
                                    render: (_, record) => (
                                      <InputNumber value={record.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix={<DollarOutlined />} />
                                    ),
                                  },
                                  {
                                    title: 'Action',
                                    key: 'action',
                                    width: 100,
                                    render: (_, __, resIndex) => (
                                      <Popconfirm
                                        title="Remove this resolution?"
                                        onConfirm={() => handleRemoveImageResolution(operationTypeObj.key, modelIndex, ratioIndex, resIndex)}
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                                      </Popconfirm>
                                    ),
                                  },
                                ]}
                              />
                            )}
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              style={{ marginTop: 8 }}
                              onClick={() => setAddImageResolutionDialog({
                                visible: true,
                                operationType: operationTypeObj.key,
                                modelIndex,
                                ratioIndex,
                                resolutionName: '',
                                resolutionValue: '',
                                actualCost: 0,
                              })}
                            >
                              Add Resolution
                            </Button>
                            </>
                          ),
                        }))}
                      />
                    ) : (
                      /* Flat pricing: aspect ratio → cost (no resolution) */
                      <Table
                        dataSource={model.aspect_ratios}
                        rowKey={(_, idx) => idx}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Aspect Ratio',
                            key: 'ratio',
                            render: (_, record) => (
                              <Space>
                                <Tag color="blue">{record.ratio.name}</Tag>
                                <span style={{ fontSize: '12px', color: '#999' }}>({record.ratio.api_enum_value})</span>
                              </Space>
                            ),
                          },
                          {
                            title: 'Actual Cost ($)',
                            key: 'actual_cost',
                            width: 150,
                            render: (_, record, ratioIndex) => (
                              <InputNumber
                                value={record.actual_cost ?? 0}
                                onChange={(val) => handleImageAspectRatioCostChange(operationTypeObj.key, modelIndex, ratioIndex, 'actual_cost', val)}
                                min={0}
                                step={0.001}
                                style={{ width: '100%' }}
                                prefix="$"
                              />
                            ),
                          },
                          {
                            title: 'Credit Cost',
                            key: 'cost',
                            width: 150,
                            render: (_, record) => (
                              <InputNumber value={record.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix={<DollarOutlined />} />
                            ),
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            width: 100,
                            render: (_, __, ratioIndex) => (
                              <Popconfirm
                                title="Remove this aspect ratio?"
                                onConfirm={() => handleRemoveImageAspectRatio(operationTypeObj.key, modelIndex, ratioIndex)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                              </Popconfirm>
                            ),
                          },
                        ]}
                      />
                    )}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      style={{ marginTop: 12 }}
                      onClick={() => setAddImageAspectRatioDialog({
                        visible: true,
                        operationType: operationTypeObj.key,
                        modelIndex,
                        selectedRatio: 'WIDESCREEN',
                        actualCost: 0,
                      })}
                    >
                      Add Aspect Ratio
                    </Button>
                      </>
                    ),
                  }]} />
                      </SortableModelWrapper>
                    ))}
                  </SortableContext>
                </DndContext>
              )}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                style={{ marginTop: 8 }}
                block
                onClick={() => setAddImageModelDialog({
                  visible: true,
                  operationType: operationTypeObj.key,
                  modelId: '',
                  modelName: '',
                  modelDescription: '',
                  modelEndpoint: '',
                  icon: '',
                  iconFile: null,
                  costPreview: 1,
                  isDefault: false,
                  hasResolution: false,
                })}
              >
                Add Model to {operationTypeObj.label}
              </Button>
            </Card>
          );
        })}

        {/* Save Button */}
        <div style={{ textAlign: 'center', marginTop: 32, marginBottom: 32 }}>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            onClick={handleImageCreditConfigUpdate}
            style={{ minWidth: 250, height: 48, fontSize: '16px' }}
          >
            Save All Image Config Changes
          </Button>
        </div>

        {/* Add Image Model Modal */}
        <Modal
          title="Add Image Model"
          open={addImageModelDialog.visible}
          onOk={() => handleAddImageModel(addImageModelDialog.operationType)}
          onCancel={() => setAddImageModelDialog({ ...addImageModelDialog, visible: false })}
          width={600}
        >
          <Form layout="vertical">
            <Form.Item label="Model ID" required>
              <Input
                value={addImageModelDialog.modelId}
                onChange={(e) => setAddImageModelDialog({ ...addImageModelDialog, modelId: e.target.value })}
                placeholder="e.g., flux-pro-text-to-image"
              />
            </Form.Item>
            <Form.Item label="Model Name" required>
              <Input
                value={addImageModelDialog.modelName}
                onChange={(e) => setAddImageModelDialog({ ...addImageModelDialog, modelName: e.target.value })}
                placeholder="e.g., Flux Pro"
              />
            </Form.Item>
            <Form.Item label="Model Endpoint">
              <Input
                value={addImageModelDialog.modelEndpoint}
                onChange={(e) => setAddImageModelDialog({ ...addImageModelDialog, modelEndpoint: e.target.value })}
                placeholder="API endpoint"
              />
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                value={addImageModelDialog.modelDescription}
                onChange={(e) => setAddImageModelDialog({ ...addImageModelDialog, modelDescription: e.target.value })}
                rows={2}
              />
            </Form.Item>
            <Form.Item label="Icon">
              <Upload
                accept=".svg,.png,.jpg,.jpeg"
                maxCount={1}
                beforeUpload={(file) => {
                  if (file.size > 5 * 1024 * 1024) {
                    message.error('Icon must be less than 5MB');
                    return Upload.LIST_IGNORE;
                  }
                  setAddImageModelDialog({ ...addImageModelDialog, iconFile: file });
                  return false;
                }}
                onRemove={() => setAddImageModelDialog({ ...addImageModelDialog, iconFile: null })}
              >
                <Button icon={<UploadOutlined />}>Upload Icon</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Cost Preview">
              <Slider
                min={1}
                max={5}
                value={addImageModelDialog.costPreview}
                onChange={(val) => setAddImageModelDialog({ ...addImageModelDialog, costPreview: val })}
                marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
              />
            </Form.Item>
            <Form.Item label="Default Model">
              <Switch
                checked={addImageModelDialog.isDefault}
                onChange={(val) => setAddImageModelDialog({ ...addImageModelDialog, isDefault: val })}
              />
            </Form.Item>
            <Form.Item label="Supports Resolution (1K, 2K, 4K)" extra="Enable if this model supports different resolution tiers with different pricing">
              <Switch
                checked={addImageModelDialog.hasResolution}
                onChange={(val) => setAddImageModelDialog({ ...addImageModelDialog, hasResolution: val })}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Image Model Modal */}
        <Modal
          title="Edit Image Model"
          open={editImageModelDialog.visible}
          onOk={() => handleEditImageModel(editImageModelDialog.operationType)}
          onCancel={() => setEditImageModelDialog({ ...editImageModelDialog, visible: false })}
          width={600}
        >
          <Form layout="vertical">
            <Form.Item label="Model ID" required>
              <Input
                value={editImageModelDialog.modelId}
                onChange={(e) => setEditImageModelDialog({ ...editImageModelDialog, modelId: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Model Name" required>
              <Input
                value={editImageModelDialog.modelName}
                onChange={(e) => setEditImageModelDialog({ ...editImageModelDialog, modelName: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Model Endpoint">
              <Input
                value={editImageModelDialog.modelEndpoint}
                onChange={(e) => setEditImageModelDialog({ ...editImageModelDialog, modelEndpoint: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                value={editImageModelDialog.modelDescription}
                onChange={(e) => setEditImageModelDialog({ ...editImageModelDialog, modelDescription: e.target.value })}
                rows={2}
              />
            </Form.Item>
            <Form.Item label="Icon">
              <Space>
                {editImageModelDialog.icon && (
                  <img src={editImageModelDialog.icon} alt="icon" style={{ width: 32, height: 32, borderRadius: 4 }} />
                )}
                <Upload
                  accept=".svg,.png,.jpg,.jpeg"
                  maxCount={1}
                  beforeUpload={(file) => {
                    if (file.size > 5 * 1024 * 1024) {
                      message.error('Icon must be less than 5MB');
                      return Upload.LIST_IGNORE;
                    }
                    setEditImageModelDialog({ ...editImageModelDialog, iconFile: file });
                    return false;
                  }}
                  onRemove={() => setEditImageModelDialog({ ...editImageModelDialog, iconFile: null })}
                >
                  <Button icon={<UploadOutlined />}>Upload Icon</Button>
                </Upload>
              </Space>
            </Form.Item>
            <Form.Item label="Cost Preview">
              <Slider
                min={1}
                max={5}
                value={editImageModelDialog.costPreview}
                onChange={(val) => setEditImageModelDialog({ ...editImageModelDialog, costPreview: val })}
                marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
              />
            </Form.Item>
            <Form.Item label="Default Model">
              <Switch
                checked={editImageModelDialog.isDefault}
                onChange={(val) => setEditImageModelDialog({ ...editImageModelDialog, isDefault: val })}
              />
            </Form.Item>
            <Form.Item label="Supports Resolution (1K, 2K, 4K)" extra="Enable if this model supports different resolution tiers with different pricing">
              <Switch
                checked={editImageModelDialog.hasResolution}
                onChange={(val) => setEditImageModelDialog({ ...editImageModelDialog, hasResolution: val })}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Image Aspect Ratio Modal */}
        <Modal
          title="Add Aspect Ratio"
          open={addImageAspectRatioDialog.visible}
          onOk={() => handleAddImageAspectRatio(addImageAspectRatioDialog.operationType)}
          onCancel={() => setAddImageAspectRatioDialog({ ...addImageAspectRatioDialog, visible: false })}
        >
          <Form layout="vertical">
            <Form.Item label="Aspect Ratio">
              <Select
                value={addImageAspectRatioDialog.selectedRatio}
                onChange={(val) => setAddImageAspectRatioDialog({ ...addImageAspectRatioDialog, selectedRatio: val })}
                style={{ width: '100%' }}
              >
                {Object.entries(AspectRatioEnum).map(([key, info]) => (
                  <Option key={key} value={key}>{info.x}x{info.y}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Actual Cost ($/image)">
              <InputNumber
                value={addImageAspectRatioDialog.actualCost}
                onChange={(val) => setAddImageAspectRatioDialog({ ...addImageAspectRatioDialog, actualCost: val || 0 })}
                min={0}
                step={0.001}
                style={{ width: '100%' }}
                prefix="$"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Image Resolution Modal */}
        <Modal
          title="Add Resolution"
          open={addImageResolutionDialog.visible}
          onOk={() => handleAddImageResolution(addImageResolutionDialog.operationType)}
          onCancel={() => setAddImageResolutionDialog({ ...addImageResolutionDialog, visible: false })}
        >
          <Form layout="vertical">
            <Form.Item label="Resolution Name" required>
              <Input
                value={addImageResolutionDialog.resolutionName}
                onChange={(e) => setAddImageResolutionDialog({ ...addImageResolutionDialog, resolutionName: e.target.value })}
                placeholder="e.g., 1K, 2K, 4K"
              />
            </Form.Item>
            <Form.Item label="Resolution Value (API)" required>
              <Input
                value={addImageResolutionDialog.resolutionValue}
                onChange={(e) => setAddImageResolutionDialog({ ...addImageResolutionDialog, resolutionValue: e.target.value })}
                placeholder="e.g., 1024, 2048, 4096"
              />
            </Form.Item>
            <Form.Item label="Actual Cost ($/image)">
              <InputNumber
                value={addImageResolutionDialog.actualCost}
                onChange={(val) => setAddImageResolutionDialog({ ...addImageResolutionDialog, actualCost: val || 0 })}
                min={0}
                step={0.001}
                style={{ width: '100%' }}
                prefix="$"
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderCreditConfigForm = () => {
    if (creditConfigLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading configuration...</p>
        </div>
      );
    }

    const operationTypes = [
      { key: 'image_to_video', label: 'Image to Video', icon: '🎬' },
      { key: 'text_to_video', label: 'Text to Video', icon: '✍️' }
    ];

    // Create duration table columns
    const createDurationColumns = (operationType, modelIndex, ratioIndex, resolutionIndex, hasAudioFeature, isAudioOptional) => {
      const baseColumns = [
      {
        title: 'Duration',
        dataIndex: ['duration', 'name'],
        key: 'duration_name',
        render: (text, record) => (
          <Space>
            <ThunderboltOutlined style={{ color: '#1890ff' }} />
            <strong>{record.duration.seconds}s ({record.duration.short_name})</strong>
          </Space>
        ),
      },
      ];

      const costColumns = (hasAudioFeature && isAudioOptional) ? [
        {
          title: 'Audio Off ',
          key: 'cost_without_audio',
          width: 150,
          render: (_, record) => (
            <InputNumber
              value={record?.cost?.without_audio ?? 0}
              disabled
              min={0}
              style={{ width: '100%' }}
              prefix="💎"
            />
          ),
        },
        {
          title: 'Audio On',
          key: 'cost_with_audio',
          width: 150,
          render: (_, record) => (
            <InputNumber
              value={record?.cost?.with_audio ?? 0}
              disabled
              min={0}
              style={{ width: '100%' }}
              prefix="💎"
            />
          ),
        },
      ] : [
     {
        title: 'Cost (Credits)',
        key: 'cost',
        width: 150,
        render: (_, record) => (
          <InputNumber
            value={record?.cost ?? 0}
            disabled
            min={0}
            style={{ width: '100%' }}
            prefix="💎"
          />
        ),
      },
      ];

      return [
        ...baseColumns,
        ...costColumns,
      {
        title: 'API Value',
        dataIndex: ['duration', 'api_enum_value'],
        key: 'api_value',
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: 'Action',
        key: 'action',
        width: 100,
        render: (_, record, index) => (
          <Popconfirm
            title="Remove Duration"
            description={`Remove ${record.duration.name}?`}
              onConfirm={() => handleRemoveDuration(operationType, modelIndex, ratioIndex, resolutionIndex, index)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        ),
      },
    ];
    };
    
    return (
      <div style={{ maxWidth: '1600px' }}>
        {/* Header Section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <Title level={2} style={{ color: 'black', margin: 0 }}>
            Credit Configuration Manager
          </Title>
          <Button icon={<SaveOutlined />} onClick={handleCreditConfigUpdate}>
            Save Changes
          </Button>
        </div>
       
        {/* Operation Types */}
        {operationTypes.map(operationTypeObj => {
          const operation = creditConfig[operationTypeObj.key];
          if (!operation) return null;

          return (
            <Card
              key={operationTypeObj.key}
              title={
                <Space size="large">
                  <span style={{ fontSize: '20px' }}>{operationTypeObj.icon}</span>
                  <span style={{ fontSize: '18px', fontWeight: 600 }}>{operationTypeObj.label}</span>
                </Space>
              }
              extra={
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setAddModelDialog({
                    visible: true,
                    operationType: operationTypeObj.key,
                    modelId: '',
                    modelName: '',
                    modelDescription: '',
                    modelEndpoint: '',
                    icon: '',
                    iconFile: null,
                    costPreview: 1,
                    hasAudioFeature: true,
                    isAudioOptional: true,
                    isDefault: false
                  })}
                >
                  Add Model
                </Button>
              }
              style={{ marginBottom: 24 }}
            >
              {/* Models */}
              {(!operation.models || operation.models.length === 0) ? (
                <Empty 
                  description="No models configured yet"
                  style={{ padding: '40px' }}
                />
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleCreditModelDragEnd(operationTypeObj.key, e)}>
                  <SortableContext items={operation.models.map(m => m.id)} strategy={verticalListSortingStrategy}>
                    {operation.models.map((model, modelIndex) => (
                      <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse style={{ flex: 1, border: 'none', borderRadius: 0 }} expandIconPosition="end" items={[{
                    key: '0',
                    label: (
                      <div>
                        <Space align="start">
                          {model.icon ? (
                            <img 
                              src={model.icon} 
                              alt={model.name}
                              style={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: 4,
                                objectFit: 'cover',
                                backgroundColor: '#f0f0f0',
                                border: '1px solid #d9d9d9'
                              }}
                            />
                          ) : (
                            <Avatar 
                              size={48} 
                              shape="square" 
                              style={{ marginTop: 4, backgroundColor: '#f0f0f0' }}
                              icon={<SettingOutlined />}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <Space wrap>
                              <Badge status="processing" color="#52c41a" />
                              <strong>{model.name}</strong>
                              <Tag color="purple">{model.id}</Tag>
                              {model.is_default && (
                                <Tag color="gold">
                                  ⭐ Default Model
                                </Tag>
                              )}
                              {(model.has_audio_feature || model.has_audio_support) && (
                                <Tag color="orange">
                                  🎵 Audio {(model.is_audio_optional !== false) ? '(Optional)' : '(Always On)'}
                                </Tag>
                              )}
                              {!(model.has_audio_feature || model.has_audio_support) && <Tag color="default">No Audio</Tag>}
                              {model.cost_preview && (
                                <Tag color="cyan">
                                  💎 Cost Preview: {'$'.repeat(model.cost_preview)}
                                </Tag>
                              )}
                            </Space>
                            {model.description && (
                              <div style={{ marginTop: 8, fontSize: '13px', fontWeight: 'normal', color: '#666' }}>
                                {model.description}
                              </div>
                            )}
                            {model.model_endpoint && (
                              <div style={{ marginTop: 6, fontSize: '12px', fontWeight: 'normal', color: '#999' }}>
                                <strong>Endpoint:</strong> {model.model_endpoint}
                              </div>
                            )}
                          </div>
                        </Space>
                      </div>
                    ),
                    extra: (
                      <Space>
                        <Tooltip title="Edit Model Settings">
                          <Button 
                            type="default" 
                            size="small" 
                            icon={<SettingOutlined />}
                            onClick={() => setEditModelDialog({
                              visible: true,
                              operationType: operationTypeObj.key,
                              modelIndex,
                              modelId: model.id || '',
                              modelName: model.name,
                              modelDescription: model.description || '',
                              modelEndpoint: model.model_endpoint || '',
                              icon: model.icon || '',
                              iconFile: null,
                              costPreview: model.cost_preview || 1,
                              hasAudioFeature: model.has_audio_feature !== undefined ? model.has_audio_feature : (model.has_audio_support !== undefined ? model.has_audio_support : true),
                              isAudioOptional: model.is_audio_optional !== undefined ? model.is_audio_optional : true,
                              isDefault: model.is_default === true,
                              pricingByResolution: (() => {
                                  const map = { ...(model.pricing_by_resolution || {}) };

                                  model.aspect_ratios?.forEach(ar => {
                                    ar.resolutions?.forEach(res => {
                                      const key = res?.resolution?.api_enum_value;
                                      const pricing = res?.pricing_per_second;

                                      if (!key) return;

                                      if (map[key] === undefined && pricing !== undefined) {
                                        if (typeof pricing === "number") {
                                          map[key] = pricing;
                                        } else if (typeof pricing === "object") {
                                          map[key] = {
                                            ON: pricing.audio_on,
                                            OFF: pricing.audio_off
                                          };
                                        }
                                      }
                                    });
                                  });

                                  return map;
                                })(),
                                defaultValues: {
                                duration: model.default_values?.duration || null,
                                resolution: model.default_values?.resolution || null,
                                aspectRatio: model.default_values?.aspectRatio || null,
                                isAudio: model.default_values?.isAudio || false
                              }
                            })}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                        <Popconfirm
                          title="Remove Model"
                          description={`Remove ${model.name}?`}
                          onConfirm={() => handleRemoveModel(operationTypeObj.key, modelIndex)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button danger size="small" icon={<DeleteOutlined />}>
                            Remove
                          </Button>
                        </Popconfirm>
                    </Space>
                    ),
                    children: (
                      <>
              {/* Global Selectors */}
              {(() => {
                const activeRatios = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);
                const allRatios = model.aspect_ratios || [];
                const activeResolutions = new Set();
                const allResKeys = new Set();
                allRatios.forEach(ar => ar.resolutions?.forEach(r => allResKeys.add(r.resolution?.api_enum_value)));
                allResKeys.forEach(resKey => {
                  if (allRatios.length > 0 && allRatios.every(ar => ar.resolutions?.some(r => r.resolution?.api_enum_value === resKey)))
                    activeResolutions.add(resKey);
                });
                const activeDurations = new Set();
                const allResolutions = [];
                allRatios.forEach(ar => ar.resolutions?.forEach(res => allResolutions.push(res)));
                const allDurKeys = new Set();
                allResolutions.forEach(res => res.durations?.forEach(d => allDurKeys.add(d.duration?.api_enum_value)));
                allDurKeys.forEach(durKey => {
                  if (allResolutions.length > 0 && allResolutions.every(res => res.durations?.some(d => d.duration?.api_enum_value === durKey)))
                    activeDurations.add(durKey);
                });
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>ASPECT RATIOS</div>
                      <Space wrap size={8}>
                        {Object.entries(AspectRatioEnum).map(([key, val]) => (
                          <Button key={key} size="small"
                            onClick={() => handleToggleAspectRatioGlobal(operationTypeObj.key, modelIndex, key)}
                            style={activeRatios.has(key) ? { borderColor: '#1677ff', color: '#1677ff', background: '#e6f4ff' } : {}}
                          >{val.ratio}</Button>
                        ))}
                      </Space>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>RESOLUTIONS</div>
                      <Space wrap size={8}>
                        {getResolutionOptionsOrdered().map(key => (
                          <Button key={key} size="small"
                            onClick={() => handleToggleResolutionGlobal(operationTypeObj.key, modelIndex, key)}
                            style={activeResolutions.has(key) ? { borderColor: '#52c41a', color: '#52c41a', background: '#f6ffed' } : {}}
                          >{VideoQualityEnum[key].name}</Button>
                        ))}
                      </Space>
                    </div>
                    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>DURATIONS</div>
                      <Space wrap size={8}>
                        {Object.entries(VideoSecondsEnum).map(([key, secs]) => (
                          <Button key={key} size="small"
                            onClick={() => handleToggleDurationGlobal(operationTypeObj.key, modelIndex, key)}
                            style={activeDurations.has(key) ? { borderColor: '#fa8c16', color: '#fa8c16', background: '#fff7e6' } : {}}
                          >{secs}s</Button>
                        ))}
                      </Space>
                    </div>
                  </div>
                );
              })()}
              {/* Aspect Ratios */}
              {model.aspect_ratios && model.aspect_ratios.length > 0 && (
                <Collapse
                  accordion
                  expandIconPosition="end"
                  items={model.aspect_ratios.map((aspectRatio, ratioIndex) => ({
                    key: ratioIndex,
                    label: (
                      <Space>
                        <Badge status="processing" color="#1890ff" />
                        <strong>{aspectRatio.ratio.name}</strong>
                        <Tag>{aspectRatio.ratio.short_name}</Tag>
                        <span style={{ color: '#999' }}>
                          ({aspectRatio.ratio.width}x{aspectRatio.ratio.height})
                        </span>
                      </Space>
                    ),
                    extra: (
                      <Popconfirm
                        title="Remove Aspect Ratio"
                        description={`Remove ${aspectRatio.ratio.name}?`}
                        onConfirm={(e) => {
                          e.stopPropagation();
                          handleRemoveAspectRatio(operationTypeObj.key, modelIndex, ratioIndex);
                        }}
                        okText="Yes"
                        cancelText="No"
                        onCancel={(e) => e.stopPropagation()}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Remove
                        </Button>
                      </Popconfirm>
                    ),
                    children: (
                      <>
                            {aspectRatio.resolutions?.map((resolution, resolutionIndex) => (
                      <Card
                        key={resolutionIndex}
                        type="inner"
                        size="small"
                       title={
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <Tag color="green">{resolution.resolution.name}</Tag>

                          <span>
                            {resolution.resolution.width}x{resolution.resolution.height}
                          </span>

                          <Tag color="blue">
                            {resolution.resolution.api_enum_value}
                          </Tag>

                          {(() => {
                            const hasAudio = model?.has_audio_feature === true;

                            const resKey = resolution?.resolution?.api_enum_value;
                            const pricing = resolution?.pricing_per_second

                            if (!resKey || !pricing) return null;

                            if (hasAudio) {

                        // AUDIO OPTIONAL
                        if (typeof pricing === "object") {
                          return (
                            <>
                              {pricing.audio_off !== undefined && (
                                <Tag color="orange" style={{ fontSize: 11 }}>
                                  OFF: {pricing.audio_off} $/sec
                                </Tag>
                              )}

                              {pricing.audio_on !== undefined && (
                                <Tag color="purple" style={{ fontSize: 11 }}>
                                  ON: {pricing.audio_on} $/sec
                                </Tag>
                              )}
                            </>
                          );
                        }

                        // AUDIO ALWAYS ON
                        if (typeof pricing === "number") {
                          return (
                            <Tag color="purple" style={{ fontSize: 11 }}>
                              ON: {pricing} $/sec
                            </Tag>
                          );
                        }
                      }

                      if (typeof pricing === "number") {
                        return (
                          <Tag color="purple" style={{ fontSize: 11 }}>
                            {pricing} $/sec
                          </Tag>
                        );
                      }

                      return null;
                    })()}
                  </div>
                }
                        extra={
                          <Popconfirm
                            title="Remove Resolution"
                            description={`Remove ${resolution.resolution.name}?`}
                                    onConfirm={() => handleRemoveResolution(operationTypeObj.key, modelIndex, ratioIndex, resolutionIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>
                              Remove
                            </Button>
                          </Popconfirm>
                        }
                        style={{ marginBottom: 16 }}
                      >
                        <Table
                                  columns={createDurationColumns(
                                    operationTypeObj.key, 
                                    modelIndex, 
                                    ratioIndex, 
                                    resolutionIndex, 
                                    model.has_audio_feature || model.has_audio_support, 
                                    model.is_audio_optional !== false
                                  )}
                                  dataSource={resolution.durations || []}
                          pagination={false}
                          size="small"
                          bordered
                        />
                        {(() => {
                          const existingKeys = new Set(resolution.durations?.map(d => d.duration?.api_enum_value) || []);
                          const available = Object.entries(VideoSecondsEnum).filter(([k]) => !existingKeys.has(k));
                          if (available.length === 0) return null;
                          return (
                            <Select
                              size="small"
                              style={{ width: '100%', marginTop: 8 }}
                              placeholder="+ Add duration to this resolution..."
                              value={null}
                              onChange={(durationKey) => {
                                handleAddDuration(operationTypeObj.key, modelIndex, ratioIndex, resolutionIndex, durationKey);
                              }}
                            >
                              {available.map(([key, secs]) => (
                                <Option key={key} value={key}>{secs}s — {key}</Option>
                              ))}
                            </Select>
                          );
                        })()}
                      </Card>
                    ))}

                      </>
                    ),
                  }))}
                />
              )}

                      </>
                    ),
                  }]} />
                      </SortableModelWrapper>
                    ))}
                  </SortableContext>
                </DndContext>
              )}

            </Card>
          );
        })}


        {/* Add Model Modal */}
        <Modal
          title="Add Model"
          open={addModelDialog.visible}
          onOk={async () => {
            if (!addModelDialog.modelId || !addModelDialog.modelName) {
              message.error('Please fill in Model ID and Model Name');
              return;
            }
            await handleAddModel(
              addModelDialog.operationType,
              addModelDialog.modelId,
              addModelDialog.modelName,
              addModelDialog.modelDescription,
              addModelDialog.modelEndpoint,
              addModelDialog.icon,
              addModelDialog.iconFile,
              addModelDialog.costPreview,
              addModelDialog.hasAudioFeature,
              addModelDialog.isAudioOptional,
              addModelDialog.isDefault,
              addModelDialog.defaultValues,
              addModelDialog.pricingByResolution
            );
            setAddModelDialog({ 
              ...addModelDialog, 
              visible: false, 
              modelId: '', 
              modelName: '',
              modelDescription: '',
              modelEndpoint: '',
              icon: '',
              iconFile: null,
              costPreview: 1,
              isDefault: false,
              pricingByResolution: {},
              defaultValues: {
                duration: null,
                resolution: null,
                aspectRatio: null,
                isAudio: false
              }
            });
          }}
          onCancel={() => setAddModelDialog({ ...addModelDialog, visible: false })}
        >
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Model ID" required>
              <Input
                value={addModelDialog.modelId}
                onChange={(e) => setAddModelDialog({ ...addModelDialog, modelId: e.target.value })}
                placeholder="e.g., fal-ai/veo3/fast"
              />
            </Form.Item>
            <Form.Item label="Model Name" required>
              <Input
                value={addModelDialog.modelName}
                onChange={(e) => setAddModelDialog({ ...addModelDialog, modelName: e.target.value })}
                placeholder="e.g., Veo 3 Fast"
              />
            </Form.Item>
            <Form.Item label="Model Endpoint">
              <Input
                value={addModelDialog.modelEndpoint}
                onChange={(e) => setAddModelDialog({ ...addModelDialog, modelEndpoint: e.target.value })}
                placeholder="e.g., https://api.example.com/v1/generate (optional)"
              />
            </Form.Item>
            <Form.Item label="Model Icon">
              <Upload
                accept="image/*,image/svg+xml"
                maxCount={1}
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
                  if (!isImage) {
                    message.error('You can only upload image files!');
                    return Upload.LIST_IGNORE;
                  }
                  const isLt5M = file.size / 1024 / 1024 < 5;
                  if (!isLt5M) {
                    message.error('Image must be smaller than 5MB!');
                    return Upload.LIST_IGNORE;
                  }
                  setAddModelDialog({ ...addModelDialog, iconFile: file });
                  return false;
                }}
                onRemove={() => {
                  setAddModelDialog({ ...addModelDialog, iconFile: null });
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Icon (Optional)</Button>
              </Upload>
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Upload an icon for this model (SVG, PNG, JPG - max 5MB)
              </p>
            </Form.Item>
            <Form.Item label="Cost Preview">
              <Slider
                min={1}
                max={5}
                value={addModelDialog.costPreview || 1}
                onChange={(val) => setAddModelDialog({ ...addModelDialog, costPreview: val })}
                marks={{
                  1: '$',
                  2: '$$',
                  3: '$$$',
                  4: '$$$$',
                  5: '$$$$$'
                }}
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Visual indicator of relative cost (1-5)
              </p>
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                value={addModelDialog.modelDescription}
                onChange={(e) => setAddModelDialog({ ...addModelDialog, modelDescription: e.target.value })}
                placeholder="Brief description of the model (optional)"
                rows={3}
              />
            </Form.Item>
            <Divider />
            <Form.Item label="Has Audio Feature">
              <Switch
                checked={addModelDialog.hasAudioFeature}
                onChange={(val) => setAddModelDialog({ ...addModelDialog, hasAudioFeature: val })}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Does this model support audio generation?
              </p>
            </Form.Item>
            {addModelDialog.hasAudioFeature && (
              <Form.Item label="Is Audio Optional">
                <Switch
                  checked={addModelDialog.isAudioOptional}
                  onChange={(val) => setAddModelDialog({ ...addModelDialog, isAudioOptional: val })}
                  checkedChildren="Optional"
                  unCheckedChildren="Always On"
                />
                <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                  If optional, users can choose with/without audio (separate costs). If always on, audio is included in all generations.
                </p>
              </Form.Item>
            )}
            <Divider orientation="left">
              <Space>
                <DollarOutlined />
                <span>Resolution Pricing ($/sec)</span>
              </Space>
            </Divider>
            {(() => {
              const hasAudio = addModelDialog.hasAudioFeature;
              const isOptional = addModelDialog.isAudioOptional;
              const isDualPricing = hasAudio && isOptional;
              const normalized = ensurePricingShape(hasAudio, isOptional, addModelDialog.pricingByResolution)
              const resOptions = getResolutionOptionsOrdered();
              const usedKeys = new Set(Object.keys(normalized || {}));
              const available = resOptions.filter((k) => !usedKeys.has(k));

              const rows = Object.keys(normalized || {})
                .sort((a, b) => QUALITY_ORDER.indexOf(a) - QUALITY_ORDER.indexOf(b))
                .map((resKey) => {
                  const val = normalized[resKey];
                  return {
                    key: resKey,
                    resKey,
                    on: hasAudio
                      ? (typeof val === "number" ? val : val?.ON)
                      : undefined,

                    off: hasAudio
                      ? (typeof val === "object" ? val?.OFF : undefined)
                      : undefined,
                    single: (!(hasAudio)) ? val : undefined,
                  };
                });

              const addRow = () => {
                if (available.length === 0) return message.warning("All resolutions already added.");
                const resKey = available[0];
                setAddModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio,isOptional, prev.pricingByResolution)) };
                  if (hasAudio && isOptional) {
                    nextMap[resKey] = { ON: undefined, OFF: undefined };
                  } else {
                    nextMap[resKey] = undefined;
                  }
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const removeRow = (resKey) => {
                setAddModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio,isOptional, prev.pricingByResolution)) };
                  delete nextMap[resKey];
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const changeResKey = (oldKey, newKey) => {
                setAddModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)) };
                  if (nextMap[newKey] !== undefined) return prev; // already used
                  nextMap[newKey] = nextMap[oldKey];
                  delete nextMap[oldKey];
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

               const setPrice = (resKey, field, val) => {
                setAddModelDialog((prev) => {
                  const hasAudio = prev.hasAudioFeature;
                  const isOptional = prev.isAudioOptional;
                  const isDualPricing = hasAudio && isOptional;

                  const nextMap = {
                    ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)),
                  };

                  if (isDualPricing) {
                    const cur =
                      nextMap[resKey] && typeof nextMap[resKey] === 'object'
                        ? nextMap[resKey]
                        : { ON: undefined, OFF: undefined };

                    nextMap[resKey] = { ...cur, [field]: val };
                  }
                  else if (hasAudio) {
                    nextMap[resKey] = val;
                  }
                  else {
                    nextMap[resKey] = val;
                  }
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

               const columns = [
                {
                  title: 'Resolution',
                  key: 'res',
                  render: (_, row) => (
                    <Select
                      value={row.resKey}
                      style={{ width: 220 }}
                      onChange={(val) => changeResKey(row.resKey, val)}
                    >
                      {resOptions.map((k) => (
                        <Option
                          key={k}
                          value={k}
                          disabled={usedKeys.has(k) && k !== row.resKey}
                        >
                          {VideoQualityEnum[k]?.name || k} ({k})
                        </Option>
                      ))}
                    </Select>
                  ),
                },

                ...(isDualPricing
                  ? [
                      {
                        title: 'OFF ($/sec)',
                        key: 'off',
                        render: (_, row) => (
                          <InputNumber
                            value={row.off}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'OFF', v)}
                            placeholder="0.0090"
                            controls={false}
                          />
                        ),
                      },
                      {
                        title: 'ON ($/sec)',
                        key: 'on',
                        render: (_, row) => (
                          <InputNumber
                            value={row.on}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'ON', v)}
                            placeholder="0.0123"
                            controls={false}
                          />
                        ),
                      },
                    ]

                  : hasAudio
                  ? [
                      {
                        title: 'ON ($/sec)',
                        key: 'on',
                        render: (_, row) => (
                          <InputNumber
                            value={row.on}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'ON', v)}
                            placeholder="0.0123"
                            controls={false}
                          />
                        ),
                      },
                    ]

                  : [
                      {
                        title: 'Price ($/sec)',
                        key: 'single',
                        render: (_, row) => (
                          <InputNumber
                            value={row.single}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'SINGLE', v)}
                            placeholder="0.0100"
                            controls={false}
                          />
                        ),
                      },
                    ]),

                {
                  title: 'Action',
                  key: 'action',
                  render: (_, row) => (
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(row.resKey)}
                    />
                  ),
                },
              ];

              return (
                <div>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} style={{ marginBottom: 10 }}>
                    Add Pricing Row
                  </Button>

                  <Table
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    size="small"
                    bordered
                    locale={{ emptyText: "No pricing rows yet" }}
                  />
                </div>
              );
            })()}
            <Divider />
            <Form.Item label="Mark as Default Model">
              <Switch
                checked={addModelDialog.isDefault}
                onChange={(val) => {
                  if (val) {
                    const defaultCount = countDefaultModels(addModelDialog.operationType);
                    if (defaultCount >= 2) {
                      message.warning(`Cannot mark more than 2 models as default for ${addModelDialog.operationType === 'image_to_video' ? 'Image to Video' : 'Text to Video'}. Please unmark another default model first.`);
                      return;
                    }
                  }
                  setAddModelDialog({ ...addModelDialog, isDefault: val });
                }}
                checkedChildren="Default"
                unCheckedChildren="Not Default"
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Mark this model as one of the default models (maximum 2 default models per operation type).
                {(() => {
                  const defaultCount = countDefaultModels(addModelDialog.operationType);
                  return ` Currently ${defaultCount} default model(s) set for ${addModelDialog.operationType === 'image_to_video' ? 'Image to Video' : 'Text to Video'}.`;
                })()}
              </p>
            </Form.Item>

            <Divider orientation="left">
              <Space>
                <SettingOutlined />
                <span>Default Selection Values</span>
              </Space>
            </Divider>
            <Alert
              message="Configure defaults after adding the model"
              description="First add aspect ratios, resolutions, and durations to this model. Then use 'Edit' to set default selection values based on the configured options."
              type="info"
              showIcon
            />
          </Form>
        </Modal>

        {/* Edit Model Modal */}
        <Modal
          title="Edit Model"
          open={editModelDialog.visible}
          onOk={async () => {
            if (!editModelDialog.modelId || !editModelDialog.modelName) {
              message.error('Model ID and Model Name are required');
              return;
            }
            await handleEditModel(
              editModelDialog.operationType,
              editModelDialog.modelIndex,
              editModelDialog.modelId,
              editModelDialog.modelName,
              editModelDialog.modelDescription,
              editModelDialog.modelEndpoint,
              editModelDialog.icon,
              editModelDialog.iconFile,
              editModelDialog.costPreview,
              editModelDialog.hasAudioFeature,
              editModelDialog.isAudioOptional,
              editModelDialog.isDefault,
              editModelDialog.defaultValues,
              editModelDialog.pricingByResolution
            );
            setEditModelDialog({ ...editModelDialog, visible: false });
          }}
          onCancel={() => setEditModelDialog({ ...editModelDialog, visible: false })}
        >
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Model ID" required>
              <Input
                value={editModelDialog.modelId}
                onChange={(e) => setEditModelDialog({ ...editModelDialog, modelId: e.target.value })}
                placeholder="e.g., fal-ai/veo3/fast"
              />
            </Form.Item>
            <Form.Item label="Model Name" required>
              <Input
                value={editModelDialog.modelName}
                onChange={(e) => setEditModelDialog({ ...editModelDialog, modelName: e.target.value })}
                placeholder="e.g., Veo 3 Fast"
              />
            </Form.Item>
            <Form.Item label="Model Endpoint">
              <Input
                value={editModelDialog.modelEndpoint}
                onChange={(e) => setEditModelDialog({ ...editModelDialog, modelEndpoint: e.target.value })}
                placeholder="e.g., https://api.example.com/v1/generate (optional)"
              />
            </Form.Item>
            <Form.Item label="Model Icon">
              {editModelDialog.icon && !editModelDialog.iconFile && (
                <div style={{ marginBottom: 8 }}>
                  <img 
                    src={editModelDialog.icon} 
                    alt="Model Icon"
                    style={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: 4,
                      objectFit: 'cover',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d9d9d9'
                    }}
                  />
                  <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>Current icon</p>
                </div>
              )}
              <Upload
                accept="image/*,image/svg+xml"
                maxCount={1}
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
                  if (!isImage) {
                    message.error('You can only upload image files!');
                    return Upload.LIST_IGNORE;
                  }
                  const isLt5M = file.size / 1024 / 1024 < 5;
                  if (!isLt5M) {
                    message.error('Image must be smaller than 5MB!');
                    return Upload.LIST_IGNORE;
                  }
                  setEditModelDialog({ ...editModelDialog, iconFile: file });
                  return false;
                }}
                onRemove={() => {
                  setEditModelDialog({ ...editModelDialog, iconFile: null });
                }}
              >
                <Button icon={<UploadOutlined />}>
                  {editModelDialog.icon ? 'Change Icon' : 'Upload Icon (Optional)'}
                </Button>
              </Upload>
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Upload a new icon to replace the current one (SVG, PNG, JPG - max 5MB)
              </p>
            </Form.Item>
            <Form.Item label="Cost Preview">
              <Slider
                min={1}
                max={5}
                value={editModelDialog.costPreview || 1}
                onChange={(val) => setEditModelDialog({ ...editModelDialog, costPreview: val })}
                marks={{
                  1: '$',
                  2: '$$',
                  3: '$$$',
                  4: '$$$$',
                  5: '$$$$$'
                }}
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Visual indicator of relative cost (1-5)
              </p>
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                value={editModelDialog.modelDescription}
                onChange={(e) => setEditModelDialog({ ...editModelDialog, modelDescription: e.target.value })}
                placeholder="Brief description of the model (optional)"
                rows={3}
              />
            </Form.Item>
            <Divider />
            <Form.Item label="Has Audio Feature">
              <Switch
                checked={editModelDialog.hasAudioFeature}
                onChange={(val) => setEditModelDialog({ ...editModelDialog, hasAudioFeature: val })}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Does this model support audio generation?
              </p>
            </Form.Item>
            {editModelDialog.hasAudioFeature && (
              <Form.Item label="Is Audio Optional">
                <Switch
                  checked={editModelDialog.isAudioOptional}
                  onChange={(val) => setEditModelDialog({ ...editModelDialog, isAudioOptional: val })}
                  checkedChildren="Optional"
                  unCheckedChildren="Always On"
                />
                <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                  If optional, users can choose with/without audio (separate costs). If always on, audio is included in all generations.
                </p>
              </Form.Item>
            )}
            <Divider orientation="left">
              <Space>
                <DollarOutlined />
                <span>Resolution Pricing ($/sec)</span>
              </Space>
            </Divider>
            {(() => {
              const hasAudio = editModelDialog.hasAudioFeature;
              const isOptional = editModelDialog.isAudioOptional;
              const isDualPricing = hasAudio && isOptional;

              const normalized = ensurePricingShape(hasAudio, isOptional, editModelDialog.pricingByResolution);
              const resOptions = getResolutionOptionsOrdered();
              const usedKeys = new Set(Object.keys(normalized || {}));
              const available = resOptions.filter((k) => !usedKeys.has(k));

              const rows = Object.keys(normalized || {})
                .sort((a, b) => QUALITY_ORDER.indexOf(a) - QUALITY_ORDER.indexOf(b))
                .map((resKey) => {
                  const val = normalized[resKey];
                  return {
                    key: resKey,
                    resKey,
                    on: hasAudio
                      ? (typeof val === "number" ? val : val?.ON)
                      : undefined,

                    off: hasAudio
                      ? (typeof val === "object" ? val?.OFF : undefined)
                      : undefined,
                    single: (!(hasAudio)) ? val : undefined,
                  };
                });

              const addRow = () => {
                if (available.length === 0) return message.warning("All resolutions already added.");
                const resKey = available[0];
                setEditModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)) };
                  if (hasAudio && isOptional) {
                    nextMap[resKey] = { ON: undefined, OFF: undefined };
                  } else {
                    nextMap[resKey] = undefined;
                  }
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const removeRow = (resKey) => {
                setEditModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)) };
                  delete nextMap[resKey];
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const changeResKey = (oldKey, newKey) => {
                setEditModelDialog((prev) => {
                  const nextMap = { ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)) };
                  if (nextMap[newKey] !== undefined) return prev; // already used
                  nextMap[newKey] = nextMap[oldKey];
                  delete nextMap[oldKey];
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const setPrice = (resKey, field, val) => {
                setEditModelDialog((prev) => {
                  const hasAudio = prev.hasAudioFeature;
                  const isOptional = prev.isAudioOptional;
                  const isDualPricing = hasAudio && isOptional;

                  const nextMap = {
                    ...(ensurePricingShape(hasAudio, isOptional, prev.pricingByResolution)),
                  };

                  if (isDualPricing) {
                    const cur =
                      nextMap[resKey] && typeof nextMap[resKey] === 'object'
                        ? nextMap[resKey]
                        : { ON: undefined, OFF: undefined };

                    nextMap[resKey] = { ...cur, [field]: val };
                  }
                  else if (hasAudio) {
                    nextMap[resKey] = val;
                  }
                  else {
                    nextMap[resKey] = val;
                  }
                  return { ...prev, pricingByResolution: nextMap };
                });
              };

              const columns = [
                {
                  title: 'Resolution',
                  key: 'res',
                  render: (_, row) => (
                    <Select
                      value={row.resKey}
                      style={{ width: 220 }}
                      onChange={(val) => changeResKey(row.resKey, val)}
                    >
                      {resOptions.map((k) => (
                        <Option
                          key={k}
                          value={k}
                          disabled={usedKeys.has(k) && k !== row.resKey}
                        >
                          {VideoQualityEnum[k]?.name || k} ({k})
                        </Option>
                      ))}
                    </Select>
                  ),
                },

                // 🔥 AUDIO OPTIONAL → ON + OFF
                ...(isDualPricing
                  ? [
                      {
                        title: 'OFF ($/sec)',
                        key: 'off',
                        render: (_, row) => (
                          <InputNumber
                            value={row.off}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'OFF', v)}
                            placeholder="0.0090"
                            controls={false}
                          />
                        ),
                      },
                      {
                        title: 'ON ($/sec)',
                        key: 'on',
                        render: (_, row) => (
                          <InputNumber
                            value={row.on}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'ON', v)}
                            placeholder="0.0123"
                            controls={false}
                          />
                        ),
                      },
                    ]
                  : hasAudio
                  ? [
                      {
                        title: 'ON ($/sec)',
                        key: 'on',
                        render: (_, row) => (
                          <InputNumber
                            value={row.on}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'ON', v)}
                            placeholder="0.0123"
                            controls={false}
                          />
                        ),
                      },
                    ]
                  : [
                      {
                        title: 'Price ($/sec)',
                        key: 'single',
                        render: (_, row) => (
                          <InputNumber
                            value={row.single}
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(v) => setPrice(row.resKey, 'SINGLE', v)}
                            placeholder="0.0100"
                            controls={false}
                          />
                        ),
                      },
                    ]),
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, row) => (
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(row.resKey)}
                    />
                  ),
                },
              ];

              return (
                <div>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} style={{ marginBottom: 10 }}>
                    Add Pricing Row
                  </Button>

                  <Table
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    size="small"
                    bordered
                    locale={{ emptyText: "No pricing rows yet" }}
                  />
                </div>
              );
            })()}
            <Divider />
            <Form.Item label="Mark as Default Model">
              <Switch
                checked={editModelDialog.isDefault}
                onChange={(val) => {
                  if (val) {
                    const currentModel = creditConfig[editModelDialog.operationType]?.models[editModelDialog.modelIndex];
                    const defaultCount = countDefaultModels(editModelDialog.operationType);
                    // If current model is not default and we're trying to make it default, check count
                    if (!currentModel?.is_default && defaultCount >= 2) {
                      message.warning(`Cannot mark more than 2 models as default for ${editModelDialog.operationType === 'image_to_video' ? 'Image to Video' : 'Text to Video'}. Please unmark another default model first.`);
                      return;
                    }
                  }
                  setEditModelDialog({ ...editModelDialog, isDefault: val });
                }}
                checkedChildren="Default"
                unCheckedChildren="Not Default"
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Mark this model as one of the default models (maximum 2 default models per operation type).
                {(() => {
                  const defaultCount = countDefaultModels(editModelDialog.operationType);
                  const currentModel = creditConfig[editModelDialog.operationType]?.models[editModelDialog.modelIndex];
                  const effectiveCount = currentModel?.is_default && !editModelDialog.isDefault 
                    ? defaultCount - 1 
                    : (!currentModel?.is_default && editModelDialog.isDefault 
                      ? defaultCount + 1 
                      : defaultCount);
                  return ` Currently ${effectiveCount} default model(s) set for ${editModelDialog.operationType === 'image_to_video' ? 'Image to Video' : 'Text to Video'}.`;
                })()}
              </p>
            </Form.Item>

            <Divider orientation="left">
              <Space>
                <SettingOutlined />
                <span>Default Selection Values</span>
              </Space>
            </Divider>
            <Alert
              message="Pre-select options when users choose this model"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {(() => {
              // Get the current model's supported options
              const model = creditConfig[editModelDialog.operationType]?.models[editModelDialog.modelIndex];
              
              // Extract unique supported values from model configuration
              const supportedAspectRatios = new Set();
              const supportedResolutions = new Set();
              const supportedDurations = new Set();
              
              model?.aspect_ratios?.forEach(ar => {
                if (ar.ratio?.api_enum_value) {
                  supportedAspectRatios.add(ar.ratio.api_enum_value);
                }
                ar.resolutions?.forEach(res => {
                  if (res.resolution?.api_enum_value) {
                    supportedResolutions.add(res.resolution.api_enum_value);
                  }
                  res.durations?.forEach(dur => {
                    if (dur.duration?.api_enum_value) {
                      supportedDurations.add(dur.duration.api_enum_value);
                    }
                  });
                });
              });
              
              const hasConfig = supportedAspectRatios.size > 0 || supportedResolutions.size > 0 || supportedDurations.size > 0;
              
              if (!hasConfig) {
                return (
                  <Alert
                    message="No configurations available"
                    description="Add aspect ratios, resolutions, and durations to this model first, then you can set default values."
                    type="warning"
                    showIcon
                  />
                );
              }
              
              return (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Default Aspect Ratio">
                        <Select
                          value={editModelDialog.defaultValues?.aspectRatio}
                          onChange={(val) => setEditModelDialog({ 
                            ...editModelDialog, 
                            defaultValues: { ...editModelDialog.defaultValues, aspectRatio: val }
                          })}
                          placeholder={supportedAspectRatios.size > 0 ? "Select default aspect ratio" : "No aspect ratios configured"}
                          allowClear
                          disabled={supportedAspectRatios.size === 0}
                        >
                          {Array.from(supportedAspectRatios).map(key => {
                            const value = AspectRatioEnum[key];
                            return (
                              <Option key={key} value={key}>
                                {value?.name || key} ({value?.ratio || key})
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Default Resolution">
                        <Select
                          value={editModelDialog.defaultValues?.resolution}
                          onChange={(val) => setEditModelDialog({ 
                            ...editModelDialog, 
                            defaultValues: { ...editModelDialog.defaultValues, resolution: val }
                          })}
                          placeholder={supportedResolutions.size > 0 ? "Select default resolution" : "No resolutions configured"}
                          allowClear
                          disabled={supportedResolutions.size === 0}
                        >
                          {Array.from(supportedResolutions).map(key => {
                            const value = VideoQualityEnum[key];
                            return (
                              <Option key={key} value={key}>
                                {value?.name || key} {value ? `(${value.width}x${value.height})` : ''}
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Default Duration">
                        <Select
                          value={editModelDialog.defaultValues?.duration}
                          onChange={(val) => setEditModelDialog({ 
                            ...editModelDialog, 
                            defaultValues: { ...editModelDialog.defaultValues, duration: val }
                          })}
                          placeholder={supportedDurations.size > 0 ? "Select default duration" : "No durations configured"}
                          allowClear
                          disabled={supportedDurations.size === 0}
                        >
                          {Array.from(supportedDurations)
                            .sort((a, b) => VideoSecondsEnum[a] - VideoSecondsEnum[b])
                            .map(key => {
                              const value = VideoSecondsEnum[key];
                              return (
                                <Option key={key} value={key}>
                                  {value} seconds
                                </Option>
                              );
                            })}
                        </Select>
                      </Form.Item>
                    </Col>
                    {editModelDialog.hasAudioFeature && (
                      <Col span={12}>
                        <Form.Item label="Default Audio Setting">
                          <Switch
                            checked={editModelDialog.defaultValues?.isAudio || false}
                            onChange={(val) => setEditModelDialog({ 
                              ...editModelDialog, 
                              defaultValues: { ...editModelDialog.defaultValues, isAudio: val }
                            })}
                            checkedChildren="Audio On"
                            unCheckedChildren="Audio Off"
                          />
                          <p style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                            Pre-select audio on/off when user picks this model
                          </p>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                </>
              );
            })()}
          </Form>
        </Modal>

      </div>
    );
  };

  const items = [
    {
      key: 'catalogueStandards',
      label: 'Standards',
      children: (
        <div style={{ padding: 24 }}>
          <CatalogueStandards />
        </div>
      ),
    },
    {
      key: 'unifiedCreditConfigV2',
      label: 'Unified Video Configuration V2',
      children: (
        <div style={{ padding: 24 }}>
          <UnifiedCreditConfigV2 />
        </div>
      ),
    },
    {
      key: 'unifiedImageCreditConfigV2',
      label: 'Unified Image Configuration V2',
      children: (
        <div style={{ padding: 24 }}>
          <ImageUnifiedCreditConfigV2 />
        </div>
      ),
    },
    {
      key: 'unifiedCreditConfig',
      label: 'Unified Video Configuration',
      children: (
        <div style={{ padding: 24 }}>
          <UnifiedCreditConfig />
        </div>
      ),
    },
    {
      key: 'unifiedImageCreditConfig',
      label: 'Unified Image Configuration',
      children: (
        <div style={{ padding: 24 }}>
          <UnifiedImageCreditConfig />
        </div>
      ),
    },
    { key: 'pricingConfigTab', label: 'Pricing Config Tab', children: (
      <div style={{ padding: 24 }}>
        <PricingConfigTab />
      </div>
    ) },
    {
      key: 'moderationAppVersion',
      label: 'Moderation App Version',
      children: (
        <div style={{ padding: 24 }}>
          <ModerationAppVersion />
        </div>
      ),
    },
  ];

  return (
    <div className="system-container">
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => { setActiveTab(key); localStorage.setItem('systemPageActiveTab', key); }}
          items={items}
          className="system-tabs"
        />
      </Card>
    </div>
  );
};

export default SystemPage; 